import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useState } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, IconButton, ListItemText, MenuItem, TextField, Typography } from '@mui/material';
import { Icon, SelectMenu, BackdropLoader } from '@livable/saas-react-components';
import { format, formatISO, startOfToday } from 'date-fns';
import { useStore } from 'src/stores';
import { useDebounce } from 'src/utils/hooks/useDebounce';
import useModalFooterStyles from 'src/styles/cutomModalFooter';
import { SUBSCRIPTION_TERM, getSubscriptionTermLabel, getSubscriptionTermLengthInMonths } from 'src/constants/subscriptionTerm';
import { STATUS_PALETTE } from '../initialState';
import { SUBSCRIPTION_TEMPLATE_MATCH_APPFOLIO, SUBSCRIPTION_TEMPLATE_MATCH_REGULAR, collectDefaultPriceIdsFromSubscriptionTemplates } from './defaultSubscriptionPriceSets';
import { START_DATE_WARNING_MONTHS, SubscriptionStartDate, isStartDateFarFromToday, isStartDateLocked } from './SubscriptionStartDate';
import { SubscriptionTerm, SubscriptionTermSummary, getFirstInvoiceDate, getMonthsBetween, pluralizeMonths } from './SubscriptionTerm';
import { TermChangeConfirmation } from './TermChangeConfirmation';
import useStyles from './styles';
const formatMoney = amount => "$".concat(amount.toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}));
const REVENUE_ESTIMATE = {
  none: 'none',
  loading: 'loading',
  estimated: 'estimated',
  notBilling: 'notBilling',
  unavailable: 'unavailable'
};

/** Prefer price.minimumPrice; fall back to legacy nested tier table or smallest tier amount. */
function resolveStripePriceMinForDisplay(p, tierTable) {
  var _tierTable$tierPrice;
  if (typeof p.minimumPrice === 'number' && !Number.isNaN(p.minimumPrice)) return p.minimumPrice;
  const isTiered = (p.priceType || '').toLowerCase() === 'tiered';
  const tierAmounts = ((_tierTable$tierPrice = tierTable === null || tierTable === void 0 ? void 0 : tierTable.tierPrice) !== null && _tierTable$tierPrice !== void 0 ? _tierTable$tierPrice : []).map(t => t.amount).filter(n => typeof n === 'number');
  if (isTiered && tierTable) {
    if (typeof tierTable.minimumPrice === 'number' && !Number.isNaN(tierTable.minimumPrice)) {
      return tierTable.minimumPrice;
    }
    return tierAmounts.length > 0 ? Math.min(...tierAmounts) : null;
  }
  return null;
}
const STATUS_OPTIONS = Object.keys(STATUS_PALETTE);
const KNOWN_TERMS = new Set(Object.values(SUBSCRIPTION_TERM));
const DEFAULT_TERM = SUBSCRIPTION_TERM.Annual;
const COMPANY_PAGE_SIZE = 50;
export const SubscriptionModalContent = observer(function SubscriptionModalContent(_ref) {
  var _store$stripe$prices, _subscription$term, _subscription$prices, _subscription$billabl, _store$stripe$subscri, _store$stripe$getSubs, _store$stripe$getSubs2;
  let isOpen = _ref.isOpen,
    onClose = _ref.onClose,
    mode = _ref.mode,
    subscription = _ref.subscription,
    onSave = _ref.onSave,
    _ref$isSubmitting = _ref.isSubmitting,
    isSubmitting = _ref$isSubmitting === void 0 ? false : _ref$isSubmitting;
  const classes = useStyles();
  const modalFooterClasses = useModalFooterStyles();
  const store = useStore();
  const isEdit = mode === 'edit';
  const pricesFromStore = (_store$stripe$prices = store.stripe.prices) !== null && _store$stripe$prices !== void 0 ? _store$stripe$prices : [];
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    companyOptions = _useState2[0],
    setCompanyOptions = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    companySearchInput = _useState4[0],
    setCompanySearchInput = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    selectedAccount = _useState6[0],
    setSelectedAccount = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    companiesLoading = _useState8[0],
    setCompaniesLoading = _useState8[1];
  const _useState9 = useState(true),
    _useState0 = _slicedToArray(_useState9, 2),
    applyForAllProperties = _useState0[0],
    setApplyForAllProperties = _useState0[1];
  const _useState1 = useState([]),
    _useState10 = _slicedToArray(_useState1, 2),
    pricings = _useState10[0],
    setPricings = _useState10[1];
  const _useState11 = useState(''),
    _useState12 = _slicedToArray(_useState11, 2),
    addPricingValue = _useState12[0],
    setAddPricingValue = _useState12[1];
  const _useState13 = useState(0),
    _useState14 = _slicedToArray(_useState13, 2),
    addPricingSelectKey = _useState14[0],
    setAddPricingSelectKey = _useState14[1];
  const _useState15 = useState(''),
    _useState16 = _slicedToArray(_useState15, 2),
    addPricingSearchKey = _useState16[0],
    setAddPricingSearchKey = _useState16[1];
  const _useState17 = useState('Draft'),
    _useState18 = _slicedToArray(_useState17, 2),
    status = _useState18[0],
    setStatus = _useState18[1];
  const _useState19 = useState(new Date()),
    _useState20 = _slicedToArray(_useState19, 2),
    startDate = _useState20[0],
    setStartDate = _useState20[1];
  const _useState21 = useState(DEFAULT_TERM),
    _useState22 = _slicedToArray(_useState21, 2),
    term = _useState22[0],
    setTerm = _useState22[1];
  const _useState23 = useState(false),
    _useState24 = _slicedToArray(_useState23, 2),
    isSubmittingLocal = _useState24[0],
    setSubmittingLocal = _useState24[1];
  const _useState25 = useState(false),
    _useState26 = _slicedToArray(_useState25, 2),
    isTermChangeConfirmOpen = _useState26[0],
    setTermChangeConfirmOpen = _useState26[1];
  const _useState27 = useState([]),
    _useState28 = _slicedToArray(_useState27, 2),
    selectedPropertyIds = _useState28[0],
    setSelectedPropertyIds = _useState28[1];
  const _useState29 = useState([]),
    _useState30 = _slicedToArray(_useState29, 2),
    companyProperties = _useState30[0],
    setCompanyProperties = _useState30[1];
  const _useState31 = useState(false),
    _useState32 = _slicedToArray(_useState31, 2),
    companyPropertiesLoading = _useState32[0],
    setCompanyPropertiesLoading = _useState32[1];
  const _useState33 = useState({
      state: REVENUE_ESTIMATE.none,
      monthly: 0
    }),
    _useState34 = _slicedToArray(_useState33, 2),
    revenueEstimate = _useState34[0],
    setRevenueEstimate = _useState34[1];
  const showSubmitLoader = isSubmitting || isSubmittingLocal;
  const startDateLocked = isStartDateLocked(isEdit, status);
  const displayedStartDate = startDateLocked && subscription !== null && subscription !== void 0 && subscription.startDate ? new Date("".concat(subscription.startDate, "T00:00:00")) : startDate;
  const startDateFieldsEnabled = !startDateLocked && !!(selectedAccount !== null && selectedAccount !== void 0 && selectedAccount.companyId);
  const previousTerm = (_subscription$term = subscription === null || subscription === void 0 ? void 0 : subscription.term) !== null && _subscription$term !== void 0 ? _subscription$term : '';
  const termShortened = isEdit && !!previousTerm && !!term && startDateFieldsEnabled && getSubscriptionTermLengthInMonths(term) < getSubscriptionTermLengthInMonths(previousTerm);
  const storedStartDate = isEdit && subscription !== null && subscription !== void 0 && subscription.startDate ? new Date("".concat(subscription.startDate, "T00:00:00")) : null;
  const isStoredStartDate = (storedStartDate === null || storedStartDate === void 0 ? void 0 : storedStartDate.getTime()) === (displayedStartDate === null || displayedStartDate === void 0 ? void 0 : displayedStartDate.getTime());
  const isStoredCombination = isEdit && isStoredStartDate && term === previousTerm;
  const nextUnbilledInvoiceDate = storedStartDate ? new Date(Math.max(getFirstInvoiceDate(storedStartDate), getFirstInvoiceDate(startOfToday()))) : null;
  /** A monthly subscription commits to nothing, so a later start date gives up nothing. */
  const isCommittedTerm = !!term && term !== SUBSCRIPTION_TERM.Monthly;
  const skippedInvoices = nextUnbilledInvoiceDate && startDateFieldsEnabled && isCommittedTerm ? getMonthsBetween(nextUnbilledInvoiceDate, getFirstInvoiceDate(displayedStartDate)) : 0;
  const pricesOnBillableUnits = ((_subscription$prices = subscription === null || subscription === void 0 ? void 0 : subscription.prices) !== null && _subscription$prices !== void 0 ? _subscription$prices : []).some(price => price.meterEvent === 'BillableUnits');
  const hasNoBillableUnits = pricesOnBillableUnits && ((_subscription$billabl = subscription === null || subscription === void 0 ? void 0 : subscription.billableUnitsCount) !== null && _subscription$billabl !== void 0 ? _subscription$billabl : 0) === 0;
  const revenueNotice = (() => {
    if (skippedInvoices === 0 || status === 'Draft') return null;
    switch (revenueEstimate.state) {
      case REVENUE_ESTIMATE.estimated:
        return revenueEstimate.monthly > 0 ? {
          variant: 'warningIcon',
          icon: 'alert',
          label: "Estimated revenue lost of ".concat(formatMoney(skippedInvoices * revenueEstimate.monthly), " over ").concat(pluralizeMonths(skippedInvoices), ", based on the current billing cycle.")
        } : {
          variant: 'info',
          icon: 'info',
          label: hasNoBillableUnits ? 'No estimated revenue loss, since there are no billable units yet.' : "No estimated revenue loss based on this subscription's current pricing."
        };
      case REVENUE_ESTIMATE.notBilling:
        return {
          variant: 'info',
          icon: 'info',
          label: "Revenue loss can't be estimated, since this subscription isn't billing yet."
        };
      case REVENUE_ESTIMATE.unavailable:
        return {
          variant: 'info',
          icon: 'info',
          label: "Revenue loss can't be estimated right now."
        };
      default:
        return null;
    }
  })();
  const subscriptions = (_store$stripe$subscri = store.stripe.subscriptionsPaged) !== null && _store$stripe$subscri !== void 0 ? _store$stripe$subscri : [];
  const accountSubscription = selectedAccount !== null && selectedAccount !== void 0 && selectedAccount.companyId && !applyForAllProperties ? subscriptions.find(s => s.companyId === selectedAccount.companyId && s.scope === 'Account' && (isEdit ? (s.id || s.subscriptionId) !== ((subscription === null || subscription === void 0 ? void 0 : subscription.id) || (subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId)) : true)) : undefined;
  const accountSubscriptionStartDate = accountSubscription === null || accountSubscription === void 0 ? void 0 : accountSubscription.startDate;
  const subscriptionTemplatesLoading = (_store$stripe$getSubs = (_store$stripe$getSubs2 = store.stripe.getSubscriptionTemplatesAsync) === null || _store$stripe$getSubs2 === void 0 ? void 0 : _store$stripe$getSubs2.inProgress) !== null && _store$stripe$getSubs !== void 0 ? _store$stripe$getSubs : false;
  const subscriptionTemplatesPlain = toJS(store.stripe.subscriptionTemplates);
  const appfolioDefaultPriceIds = collectDefaultPriceIdsFromSubscriptionTemplates(subscriptionTemplatesPlain, SUBSCRIPTION_TEMPLATE_MATCH_APPFOLIO);
  const regularDefaultPriceIds = collectDefaultPriceIdsFromSubscriptionTemplates(subscriptionTemplatesPlain, SUBSCRIPTION_TEMPLATE_MATCH_REGULAR);
  const billedSubscriptionId = isEdit ? (subscription === null || subscription === void 0 ? void 0 : subscription.id) || (subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId) : null;
  const billedCompanyId = isEdit ? subscription === null || subscription === void 0 ? void 0 : subscription.companyId : null;
  useEffect(() => {
    if (!isOpen || !billedSubscriptionId || !billedCompanyId) {
      setRevenueEstimate({
        state: REVENUE_ESTIMATE.none,
        monthly: 0
      });
      return undefined;
    }
    let isCurrentRequest = true;
    setRevenueEstimate({
      state: REVENUE_ESTIMATE.loading,
      monthly: 0
    });
    store.stripe.getSubscriptionCompanyForCompanyAsync.run({
      companyId: billedCompanyId
    }).then(resp => {
      var _resp$response, _billed$items;
      if (!isCurrentRequest) return;
      if (!(resp !== null && resp !== void 0 && resp.isSuccessful) || !Array.isArray(resp === null || resp === void 0 || (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.subscriptions)) {
        setRevenueEstimate({
          state: REVENUE_ESTIMATE.unavailable,
          monthly: 0
        });
        return;
      }
      const billed = resp.response.subscriptions.find(s => s.id === billedSubscriptionId);
      if (!billed) {
        setRevenueEstimate({
          state: REVENUE_ESTIMATE.notBilling,
          monthly: 0
        });
        return;
      }
      setRevenueEstimate({
        state: REVENUE_ESTIMATE.estimated,
        monthly: ((_billed$items = billed.items) !== null && _billed$items !== void 0 ? _billed$items : []).reduce((total, item) => {
          var _item$currentTotal;
          return total + ((_item$currentTotal = item.currentTotal) !== null && _item$currentTotal !== void 0 ? _item$currentTotal : 0);
        }, 0)
      });
    }).catch(() => {
      if (isCurrentRequest) setRevenueEstimate({
        state: REVENUE_ESTIMATE.unavailable,
        monthly: 0
      });
    });
    return () => {
      isCurrentRequest = false;
    };
  }, [isOpen, billedSubscriptionId, billedCompanyId, store.stripe]);
  const debouncedSearch = useDebounce(companySearchInput, 300);
  const fetchCompanies = useCallback(async search => {
    if (!(store.account.userProfile.isUserAdmin || store.account.userProfile.isUserSuperAdmin)) return;
    setCompaniesLoading(true);
    try {
      const resp = await store.admin.getCompaniesListAsync.run({
        page: 1,
        pageSize: COMPANY_PAGE_SIZE,
        search: search || ''
      });
      const items = Array.isArray(resp === null || resp === void 0 ? void 0 : resp.items) ? resp.items : [];
      setCompanyOptions(items);
    } finally {
      setCompaniesLoading(false);
    }
  }, [store.admin, store.account.userProfile.isUserAdmin, store.account.userProfile.isUserSuperAdmin]);
  useEffect(() => {
    if (!isOpen) return;
    fetchCompanies(debouncedSearch);
  }, [isOpen, debouncedSearch, fetchCompanies]);
  useEffect(() => {
    if (!isOpen) return;
    store.stripe.getSubscriptionTemplatesAsync.run();
  }, [isOpen, store.stripe]);
  useEffect(() => {
    if (!isOpen || !(selectedAccount !== null && selectedAccount !== void 0 && selectedAccount.companyId) || applyForAllProperties) {
      setCompanyProperties([]);
      return;
    }
    setCompanyPropertiesLoading(true);
    store.company.getCompanyPropertiesForCompanyAsync.run({
      companyId: selectedAccount.companyId
    }).then(resp => {
      if (resp !== null && resp !== void 0 && resp.isSuccessful && Array.isArray(resp === null || resp === void 0 ? void 0 : resp.response)) {
        setCompanyProperties(resp.response);
      } else {
        setCompanyProperties([]);
      }
      if (!isEdit) setSelectedPropertyIds([]);
    }).catch(() => {
      setCompanyProperties([]);
      if (!isEdit) setSelectedPropertyIds([]);
    }).finally(() => setCompanyPropertiesLoading(false));
  }, [isOpen, selectedAccount === null || selectedAccount === void 0 ? void 0 : selectedAccount.companyId, applyForAllProperties, store.company, isEdit]);
  const formatPrice = value => typeof value === 'number' && !Number.isNaN(value) ? "$".concat(Number(value).toFixed(2)) : '-';
  const mapSubscriptionPriceToForm = useCallback(p => {
    const isTiered = (p.priceType || '').toLowerCase() === 'tiered';
    const tierTable = p.priceTableResponse;
    const minPrice = resolveStripePriceMinForDisplay(p, tierTable);
    const amount = isTiered && minPrice != null ? "Min ".concat(formatPrice(minPrice)) : formatPrice(p.fixedPrice);
    return {
      id: p.id,
      code: p.id,
      name: p.priceName,
      priceName: p.priceName,
      productId: p.productId,
      productName: p.productName,
      type: p.priceType,
      amount
    };
  }, []);

  // Initialize form only when modal opens (isOpen becomes true), not on subscription reference change,
  // so that selecting an account in the dropdown is not overwritten by this effect.
  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && subscription) {
      var _ref2, _subscription$account, _ref3, _subscription$subscri, _ref4, _subscription$prices2;
      setSelectedAccount({
        companyId: subscription.companyId || '',
        companyName: subscription.accountName || subscription.companyName || '',
        email: (_ref2 = (_subscription$account = subscription.accountEmail) !== null && _subscription$account !== void 0 ? _subscription$account : subscription.companyHolderEmail) !== null && _ref2 !== void 0 ? _ref2 : ''
      });
      setApplyForAllProperties(subscription.scope === 'Account');
      setSelectedPropertyIds((_ref3 = (_subscription$subscri = subscription.subscriptionPropertyIds) !== null && _subscription$subscri !== void 0 ? _subscription$subscri : subscription.propertyIds) !== null && _ref3 !== void 0 ? _ref3 : []);
      const sourcePrices = (_ref4 = (_subscription$prices2 = subscription.prices) !== null && _subscription$prices2 !== void 0 ? _subscription$prices2 : subscription.pricings) !== null && _ref4 !== void 0 ? _ref4 : [];
      setPricings(sourcePrices.map(p => mapSubscriptionPriceToForm(p)));
      setStatus(subscription.status || 'Draft');
      setStartDate(new Date("".concat(subscription.startDate, "T00:00:00")));
      setTerm(KNOWN_TERMS.has(subscription.term) ? subscription.term : '');
    } else {
      setSelectedAccount(null);
      setCompanySearchInput('');
      setApplyForAllProperties(true);
      setPricings([]);
      setAddPricingValue('');
      setStatus('Draft');
      setStartDate(new Date());
      setTerm(DEFAULT_TERM);
      setSelectedPropertyIds([]);
      setCompanyProperties([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when modal opens
  }, [isOpen]);
  const selectedProductIds = new Set(pricings.map(p => p.productId).filter(Boolean));
  const availablePricingsToAdd = pricesFromStore.filter(p => !pricings.some(pr => (pr.id || pr.code) === p.id) && !selectedProductIds.has(p.productId));
  const availablePricingsToAddSorted = [...availablePricingsToAdd].sort((a, b) => (a.productName || '').localeCompare(b.productName || ''));
  const addPricingSearch = (addPricingSearchKey || '').trim().toLowerCase();
  const filteredPricingsForAdd = !addPricingSearch ? availablePricingsToAddSorted : availablePricingsToAddSorted.filter(p => (p.productName || '').toLowerCase().includes(addPricingSearch) || (p.priceName || '').toLowerCase().includes(addPricingSearch));
  const pricingsGroupedByProductFiltered = (() => {
    const byProduct = new Map();
    filteredPricingsForAdd.forEach(p => {
      const key = p.productId || p.productName || '';
      const name = p.productName || p.productId || 'Other';
      if (!byProduct.has(key)) byProduct.set(key, {
        productName: name,
        items: []
      });
      byProduct.get(key).items.push(p);
    });
    return [...byProduct.entries()].map(_ref5 => {
      let _ref6 = _slicedToArray(_ref5, 2),
        key = _ref6[0],
        _ref6$ = _ref6[1],
        productName = _ref6$.productName,
        items = _ref6$.items;
      return {
        key,
        productName,
        items
      };
    });
  })();
  const getPricingAmountDisplay = p => {
    const isTiered = (p.priceType || '').toLowerCase() === 'tiered';
    const minPrice = resolveStripePriceMinForDisplay(p, p.tierTableResponse);
    return isTiered && minPrice != null ? "Min ".concat(formatPrice(minPrice)) : formatPrice(p.fixedPrice);
  };
  const handleAddPricing = priceId => {
    const toAdd = pricesFromStore.find(p => p.id === priceId);
    if (!toAdd) return;
    if (pricings.some(pr => (pr.id || pr.code) === toAdd.id)) return;
    if (selectedProductIds.has(toAdd.productId)) return;
    const isTiered = (toAdd.priceType || '').toLowerCase() === 'tiered';
    const minPrice = resolveStripePriceMinForDisplay(toAdd, toAdd.tierTableResponse);
    const amount = isTiered && minPrice != null ? "Min ".concat(formatPrice(minPrice)) : formatPrice(toAdd.fixedPrice);
    setPricings(prev => {
      var _toAdd$priceType;
      return [...prev, {
        type: (_toAdd$priceType = toAdd.priceType) !== null && _toAdd$priceType !== void 0 ? _toAdd$priceType : '',
        id: toAdd.id,
        code: toAdd.id,
        name: toAdd.priceName,
        priceName: toAdd.priceName,
        productId: toAdd.productId,
        productName: toAdd.productName,
        amount
      }];
    });
    setAddPricingValue('');
  };
  const handleRemovePricing = priceId => {
    setPricings(prev => prev.filter(p => (p.id || p.code) !== priceId));
  };
  const applyDefaultPriceSet = rawIds => {
    const ids = (rawIds || []).map(id => typeof id === 'string' ? id.trim() : '').filter(Boolean);
    const next = [];
    const usedProductIds = new Set();
    for (const priceId of ids) {
      var _toAdd$priceType2;
      const toAdd = pricesFromStore.find(p => p.id === priceId);
      if (!toAdd) continue;
      if (next.some(pr => (pr.id || pr.code) === toAdd.id)) continue;
      if (usedProductIds.has(toAdd.productId)) continue;
      usedProductIds.add(toAdd.productId);
      const isTiered = (toAdd.priceType || '').toLowerCase() === 'tiered';
      const minPrice = resolveStripePriceMinForDisplay(toAdd, toAdd.tierTableResponse);
      const amount = isTiered && minPrice != null ? "Min ".concat(formatPrice(minPrice)) : formatPrice(toAdd.fixedPrice);
      next.push({
        type: (_toAdd$priceType2 = toAdd.priceType) !== null && _toAdd$priceType2 !== void 0 ? _toAdd$priceType2 : '',
        id: toAdd.id,
        code: toAdd.id,
        name: toAdd.priceName,
        priceName: toAdd.priceName,
        productId: toAdd.productId,
        productName: toAdd.productName,
        amount
      });
    }
    setPricings(next);
    setAddPricingValue('');
    setAddPricingSearchKey('');
    setAddPricingSelectKey(k => k + 1);
  };
  useEffect(() => {
    if (!isOpen) {
      setSubmittingLocal(false);
      setTermChangeConfirmOpen(false);
    }
  }, [isOpen]);
  const handleSubmit = useCallback(async () => {
    var _selectedAccount$comp, _selectedAccount$emai, _selectedAccount$comp2;
    const isValid = !!(selectedAccount !== null && selectedAccount !== void 0 && selectedAccount.companyId) && (!!term || startDateLocked);
    if (!isValid) return;
    setSubmittingLocal(true);
    const scope = applyForAllProperties ? 'Account' : 'Property';
    const resolvedStatus = isEdit ? status : 'Draft';
    const accountName = (_selectedAccount$comp = selectedAccount === null || selectedAccount === void 0 ? void 0 : selectedAccount.companyName) !== null && _selectedAccount$comp !== void 0 ? _selectedAccount$comp : '';
    const accountEmail = (_selectedAccount$emai = selectedAccount === null || selectedAccount === void 0 ? void 0 : selectedAccount.email) !== null && _selectedAccount$emai !== void 0 ? _selectedAccount$emai : '';
    const companyId = (_selectedAccount$comp2 = selectedAccount === null || selectedAccount === void 0 ? void 0 : selectedAccount.companyId) !== null && _selectedAccount$comp2 !== void 0 ? _selectedAccount$comp2 : '';
    const resolvedStartDate = formatISO(startDate !== null && startDate !== void 0 ? startDate : new Date()).split('T')[0];
    if (isEdit && subscription) {
      try {
        await store.stripe.updateSubscriptionAsync.run(_objectSpread({
          subscriptionId: subscription.id || subscription.subscriptionId || '',
          companyId,
          priceIds: pricings.map(p => p.id || p.code).filter(Boolean),
          status: resolvedStatus
        }, startDateLocked ? {} : {
          startDate: resolvedStartDate,
          term
        }));
        onSave(_objectSpread(_objectSpread({}, subscription), {}, {
          companyId,
          accountName: accountName || subscription.accountName,
          accountEmail: accountEmail || subscription.accountEmail,
          scope,
          pricings: pricings.map(p => _objectSpread({}, p)),
          status: resolvedStatus
        }, startDateLocked ? {} : {
          startDate: resolvedStartDate,
          term
        }));
        onClose();
      } catch (err) {
        console.error('Update subscription failed:', err);
      } finally {
        setSubmittingLocal(false);
      }
    } else {
      onSave({
        companyId,
        accountName,
        accountEmail,
        scope,
        startDate: resolvedStartDate,
        term,
        pricings: pricings.map(p => _objectSpread({}, p)),
        status: resolvedStatus,
        properties: 0,
        units: 0,
        propertyIds: applyForAllProperties ? [] : selectedPropertyIds
      });
      // Parent closes modal after create API succeeds
    }
  }, [selectedAccount, applyForAllProperties, isEdit, status, startDate, startDateLocked, term, subscription, pricings, selectedPropertyIds, onSave, onClose, store.stripe]);
  const handleSaveClick = () => {
    if (termShortened) {
      setTermChangeConfirmOpen(true);
      return;
    }
    handleSubmit();
  };
  const handleConfirmTermChange = () => {
    setTermChangeConfirmOpen(false);
    handleSubmit();
  };
  const accountOptions = !selectedAccount ? companyOptions : companyOptions.some(c => c.companyId === selectedAccount.companyId) ? companyOptions : [selectedAccount, ...companyOptions];
  const getOptionLabel = useCallback(option => {
    var _option$companyName;
    return (_option$companyName = option === null || option === void 0 ? void 0 : option.companyName) !== null && _option$companyName !== void 0 ? _option$companyName : '';
  }, []);
  const isOptionEqualToValue = useCallback((option, value) => (option === null || option === void 0 ? void 0 : option.companyId) === (value === null || value === void 0 ? void 0 : value.companyId), []);
  const filterOptions = useCallback(x => x, []);
  const renderInput = useCallback(params => /*#__PURE__*/React.createElement(TextField, _extends({}, params, {
    label: isEdit ? 'Account' : 'Search and select account',
    placeholder: "Type to search...",
    variant: "outlined",
    size: "small"
  })), [isEdit]);
  const renderOption = useCallback((props, option) => {
    var _option$email;
    return /*#__PURE__*/React.createElement("li", _extends({}, props, {
      key: option.companyId
    }), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1",
      fontWeight: 600
    }, option.companyName), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary"
    }, (_option$email = option.email) !== null && _option$email !== void 0 ? _option$email : '—')));
  }, []);
  const hasExistingSubscriptionForSelectedAccount = !!(selectedAccount !== null && selectedAccount !== void 0 && selectedAccount.companyId) && subscriptions.some(s => s.companyId === selectedAccount.companyId && (isEdit ? (s.id || s.subscriptionId) !== ((subscription === null || subscription === void 0 ? void 0 : subscription.id) || (subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId)) : true));
  const activeProperties = (companyProperties || []).filter(prop => (prop.propertyStatus || '') === 'Active');
  if (!isOpen) return null;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: showSubmitLoader
  }), /*#__PURE__*/React.createElement(Box, {
    className: classes.content
  }, isEdit && /*#__PURE__*/React.createElement(Box, {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    className: classes.sectionTitle
  }, "Status"), /*#__PURE__*/React.createElement(SelectMenu, {
    id: "subscription-status",
    labelId: "subscription-status-label",
    label: "Status",
    value: status,
    onChange: e => setStatus(e.target.value)
  }, STATUS_OPTIONS.map(s => /*#__PURE__*/React.createElement(MenuItem, {
    key: s,
    value: s
  }, s)))), /*#__PURE__*/React.createElement(Box, {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    className: classes.sectionTitle
  }, "Account"), hasExistingSubscriptionForSelectedAccount && !isEdit && /*#__PURE__*/React.createElement(Chip, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "alert"
    }),
    label: "This account already has an existing subscription",
    variant: "warningIcon",
    className: classes.warningChip
  }), /*#__PURE__*/React.createElement(Autocomplete, {
    id: "subscription-account",
    options: accountOptions,
    className: classes.autocomplete,
    value: selectedAccount,
    onChange: (_, newValue) => setSelectedAccount(newValue),
    disabled: isEdit,
    inputValue: companySearchInput,
    onInputChange: (_, newInputValue) => setCompanySearchInput(newInputValue),
    getOptionLabel: getOptionLabel,
    isOptionEqualToValue: isOptionEqualToValue,
    filterOptions: filterOptions,
    loading: companiesLoading,
    renderInput: renderInput,
    renderOption: renderOption,
    disablePortal: true
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    disabled: isEdit,
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: applyForAllProperties,
      onChange: e => setApplyForAllProperties(e.target.checked)
    }),
    label: /*#__PURE__*/React.createElement(Box, {
      className: classes.scopeLabel
    }, /*#__PURE__*/React.createElement("span", null, "Apply for all properties (Account subscription)"), !!accountSubscriptionStartDate && /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary"
    }, "starts ", format(new Date("".concat(accountSubscriptionStartDate, "T00:00:00")), 'MM/dd/yy'), !!(accountSubscription !== null && accountSubscription !== void 0 && accountSubscription.term) && " \xB7 ".concat(getSubscriptionTermLabel(accountSubscription.term)))),
    className: classes.checkboxLabel
  }), /*#__PURE__*/React.createElement(Box, {
    className: classes.termRow
  }, /*#__PURE__*/React.createElement(SubscriptionTerm, {
    value: term,
    onChange: setTerm,
    disabled: !startDateFieldsEnabled
  }), /*#__PURE__*/React.createElement(SubscriptionStartDate, {
    value: displayedStartDate,
    onChange: setStartDate,
    disabled: !startDateFieldsEnabled
  })), startDateFieldsEnabled && isStartDateFarFromToday(displayedStartDate) && /*#__PURE__*/React.createElement(Box, {
    className: classes.startDateWarning
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "alert",
    fontSize: "small",
    color: "warning"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    sx: {
      color: 'warning.dark'
    }
  }, "Start date is more than ", START_DATE_WARNING_MONTHS, " months from today, please confirm this is intentional.")), !!revenueNotice && /*#__PURE__*/React.createElement(Chip, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: revenueNotice.icon
    }),
    label: revenueNotice.label,
    variant: revenueNotice.variant,
    className: classes.startDateRevenue
  }), !!term && /*#__PURE__*/React.createElement(SubscriptionTermSummary, {
    term: term,
    startDate: displayedStartDate,
    storedCommitmentEndDate: isStoredCombination ? subscription === null || subscription === void 0 ? void 0 : subscription.commitmentEndDate : null
  }), !applyForAllProperties && /*#__PURE__*/React.createElement(Box, {
    className: classes.section,
    sx: {
      mt: 16
    }
  }, isEdit ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle2",
    color: "text.secondary",
    sx: {
      mb: 1
    }
  }, "Properties"), selectedPropertyIds.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "No properties selected") : /*#__PURE__*/React.createElement(Box, {
    sx: {
      m: 0,
      pl: 2.5
    }
  }, selectedPropertyIds.map(id => {
    const prop = companyProperties.find(p => p.propertyId === id);
    const label = (prop === null || prop === void 0 ? void 0 : prop.propertyAddress) || (prop === null || prop === void 0 ? void 0 : prop.propertyName) || id;
    return /*#__PURE__*/React.createElement(Typography, {
      key: id,
      variant: "body1",
      sx: {
        py: 0.25
      }
    }, label);
  }))) : /*#__PURE__*/React.createElement(SelectMenu, {
    id: "subscription-properties",
    labelId: "subscription-properties-label",
    label: "Properties",
    value: selectedPropertyIds,
    onChange: e => setSelectedPropertyIds(Array.isArray(e.target.value) ? e.target.value : []),
    multiple: true,
    renderValue: selected => selected.length ? "".concat(selected.length, " propert").concat(selected.length === 1 ? 'y' : 'ies', " selected") : ''
  }, companyPropertiesLoading ? /*#__PURE__*/React.createElement(MenuItem, {
    value: "",
    disabled: true
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }
  }, /*#__PURE__*/React.createElement(CircularProgress, {
    size: 16
  }), /*#__PURE__*/React.createElement("span", null, "Loading..."))) : companyProperties.length === 0 ? /*#__PURE__*/React.createElement(MenuItem, {
    value: "",
    disabled: true
  }, selectedAccount !== null && selectedAccount !== void 0 && selectedAccount.companyId ? 'No properties found' : 'Select an account first') : activeProperties.length === 0 ? /*#__PURE__*/React.createElement(MenuItem, {
    value: "",
    disabled: true,
    className: classes.noActiveProperties
  }, "No active properties found") : activeProperties.map(prop => /*#__PURE__*/React.createElement(MenuItem, {
    key: prop.propertyId,
    value: prop.propertyId,
    variant: "multiple"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: selectedPropertyIds.indexOf(prop.propertyId) > -1,
    size: "small"
  }), /*#__PURE__*/React.createElement(ListItemText, {
    primary: prop.propertyAddress || prop.propertyName || prop.propertyId
  })))))), /*#__PURE__*/React.createElement(Box, {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    className: classes.sectionTitle
  }, "Pricings"), /*#__PURE__*/React.createElement(Box, {
    className: classes.addControl,
    key: addPricingSelectKey
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    id: "subscription-add-pricings",
    labelId: "subscription-add-pricings-label",
    label: "Add pricings",
    value: addPricingValue,
    onChange: e => {
      const v = e.target.value;
      if (v && v !== 'headerName') {
        handleAddPricing(v);
        setAddPricingValue('');
        setAddPricingSearchKey('');
        setAddPricingSelectKey(k => k + 1);
      }
    },
    enableSearch: true,
    onSearch: setAddPricingSearchKey,
    onCloseExternal: () => setAddPricingSearchKey('')
  }, pricingsGroupedByProductFiltered.length === 0 ? /*#__PURE__*/React.createElement(MenuItem, {
    value: "",
    disabled: true
  }, "No more pricings to add") : pricingsGroupedByProductFiltered.map(_ref7 => {
    let key = _ref7.key,
      productName = _ref7.productName,
      items = _ref7.items;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: key
    }, /*#__PURE__*/React.createElement(MenuItem, {
      value: "headerName",
      onClick: ev => ev.stopPropagation(),
      className: classes.addPricingHeaderItem
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      fontWeight: 600,
      component: "span"
    }, productName)), items.map(p => /*#__PURE__*/React.createElement(MenuItem, {
      key: p.id,
      value: p.id,
      onClick: () => {
        handleAddPricing(p.id);
        setAddPricingValue('');
        setAddPricingSearchKey('');
        setAddPricingSelectKey(k => k + 1);
      }
    }, /*#__PURE__*/React.createElement(Box, {
      sx: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%'
      }
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1",
      fontWeight: 600
    }, p.priceName), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary"
    }, p.productName)), /*#__PURE__*/React.createElement(Typography, {
      variant: "body1",
      fontWeight: 600,
      sx: {
        ml: 2
      }
    }, getPricingAmountDisplay(p))))));
  }))), /*#__PURE__*/React.createElement(Box, {
    className: classes.defaultPriceSets
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    disabled: subscriptionTemplatesLoading || appfolioDefaultPriceIds.length === 0,
    onClick: () => applyDefaultPriceSet(appfolioDefaultPriceIds)
  }, "Default set of prices for AppFolio customers"), /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    disabled: subscriptionTemplatesLoading || regularDefaultPriceIds.length === 0,
    onClick: () => applyDefaultPriceSet(regularDefaultPriceIds)
  }, "Default set of prices for regular customers")), /*#__PURE__*/React.createElement(Box, {
    className: classes.list
  }, pricings.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "No pricings added.") : pricings.map(p => {
    var _p$priceName, _p$productName, _p$code, _p$type, _p$amount;
    return /*#__PURE__*/React.createElement(Box, {
      key: p.id || p.code,
      className: classes.listRow
    }, /*#__PURE__*/React.createElement(Box, {
      className: classes.listRowMain
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1",
      fontWeight: 600
    }, (_p$priceName = p.priceName) !== null && _p$priceName !== void 0 ? _p$priceName : p.name), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary"
    }, (_p$productName = p.productName) !== null && _p$productName !== void 0 ? _p$productName : "".concat((_p$code = p.code) !== null && _p$code !== void 0 ? _p$code : p.id, " - ").concat((_p$type = p.type) !== null && _p$type !== void 0 ? _p$type : ''))), /*#__PURE__*/React.createElement(Box, {
      className: classes.listRowRight
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "h5",
      fontWeight: 600
    }, (_p$amount = p.amount) !== null && _p$amount !== void 0 ? _p$amount : 'N/A'), /*#__PURE__*/React.createElement(IconButton, {
      onClick: () => handleRemovePricing(p.id || p.code),
      "aria-label": "Remove pricing"
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "close"
    }))));
  })))), /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.modalFooter
  }, /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.footerEven
  }, /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.footerWrapper
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    onClick: onClose
  }, "Cancel")), /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.footerWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.footerButton
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: handleSaveClick,
    disabled: pricings.length === 0 || !(selectedAccount !== null && selectedAccount !== void 0 && selectedAccount.companyId) || !term && !startDateLocked || showSubmitLoader,
    startIcon: showSubmitLoader ? /*#__PURE__*/React.createElement(CircularProgress, {
      size: 20,
      color: "inherit"
    }) : null
  }, showSubmitLoader ? isEdit ? 'Saving...' : 'Creating...' : isEdit ? 'Save changes' : 'Create subscription'))))), /*#__PURE__*/React.createElement(TermChangeConfirmation, {
    isOpen: isTermChangeConfirmOpen,
    onCancel: () => setTermChangeConfirmOpen(false),
    onConfirm: handleConfirmTermChange,
    accountName: selectedAccount === null || selectedAccount === void 0 ? void 0 : selectedAccount.companyName,
    previousTerm: previousTerm,
    previousStartDate: storedStartDate !== null && storedStartDate !== void 0 ? storedStartDate : displayedStartDate,
    previousRenewalDate: isStoredStartDate ? subscription === null || subscription === void 0 ? void 0 : subscription.renewalDate : null,
    previousCommitmentEndDate: isStoredStartDate ? subscription === null || subscription === void 0 ? void 0 : subscription.commitmentEndDate : null,
    term: term,
    startDate: displayedStartDate
  }));
});